import { Card } from '../../components';

import type { PolicyBlock, PolicyDoc } from './policyDoc';

/** 본문 글자(시안 `article` 본문) — 14px · 줄 높이 1.65. 한국어는 어절에서 끊는다. */
const BODY = 'break-keep text-sm leading-[1.65] tracking-[-0.07px] text-bk-700';

const Block = ({
  block,
  articleTitle,
}: {
  block: PolicyBlock;
  /** 표가 속한 조의 제목 — 밀 수 있는 표 영역의 이름으로 쓴다. */
  articleTitle: string;
}) => {
  switch (block.type) {
    case 'p':
      return <p className={BODY}>{block.text}</p>;
    case 'sub':
      return (
        <h3 className="mt-1 break-keep text-sm font-medium leading-[1.4] tracking-[-0.07px] text-bk-900">
          {block.text}
        </h3>
      );
    case 'note':
      // 시안의 붉은 안내 상자. 글자는 진한 빨강이다 — 연분홍 위의 브랜드 핑크는 본문 크기에서
      // 대비가 모자란다.
      return (
        <p className="break-keep rounded-2xl bg-[rgba(255,45,85,0.08)] px-4 py-3 text-sm leading-[1.6] tracking-[-0.07px] text-destructive">
          {block.text}
        </p>
      );
    case 'ul':
    case 'ol': {
      const List = block.type;
      return (
        <List
          className={`${BODY} flex flex-col gap-1 pl-5 ${
            block.type === 'ul' ? 'list-disc' : 'list-decimal'
          }`}
        >
          {block.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </List>
      );
    }
    case 'table':
      // 표만 가로로 민다 — 칸이 셋인 표는 320px 에서 페이지를 넘친다. 키보드로도 밀 수
      // 있게 포커스를 받고, 포커스를 받는 영역이라 이름을 갖는다(조 제목). 링은 시행일
      // 알약과 같은 검은 링이다.
      return (
        <div
          role="region"
          aria-label={articleTitle}
          tabIndex={0}
          className="overflow-x-auto rounded-xl border border-bk-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <table className="w-full min-w-[280px] border-collapse text-left text-[13px] leading-[1.5] tracking-[-0.065px] text-bk-700">
            <thead className="bg-bk-50 text-bk-900">
              <tr>
                {block.head.map((cell, index) => (
                  <th
                    key={index}
                    scope="col"
                    // 첫 칸은 행의 이름이다 — 한 글자씩 꺾이지 않게 최소 폭을 준다.
                    className="px-3 py-2 font-medium first:min-w-[5.5rem]"
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t border-bk-100">
                  {row.map((cell, index) => (
                    <td key={index} className="break-keep px-3 py-2 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
};

/**
 * 약관·방침 한 버전의 본문(Figma `MY #이용약관`·`MY #개인정보처리방침`의 `content`).
 *
 * 머리말은 카드 밖, 요약은 자기 카드, 조문은 문서 카드 하나에 담는다. 조 제목은 `h2` 다 —
 * 스크린리더의 제목 이동으로 조 사이를 건너뛴다. 화면 제목(`h1`)은 이 컴포넌트를 부르는
 * 쪽이 그린다.
 *
 * 글자는 원문 그대로다. 요약 카드도 방침이 이미 붙인 이름만 옮긴 것이다(`policyDoc.ts`).
 */
export const PolicyDocument = ({
  doc,
  summaryTitle,
}: {
  doc: PolicyDoc;
  /** 요약 카드의 제목 — 문서 언어로 넘긴다. */
  summaryTitle: string;
}) => (
  <div className="flex flex-col gap-4">
    {doc.intro && (
      <p className="break-keep px-1 text-sm leading-[1.6] tracking-[-0.07px] text-bk-700">
        {doc.intro}
      </p>
    )}

    {doc.summary && doc.summary.length > 0 && (
      <Card className="flex flex-col gap-3.5 p-5">
        <h2 className="text-base font-medium leading-[1.4] tracking-[-0.08px] text-bk-900">
          {summaryTitle}
        </h2>
        <dl className="flex flex-col divide-y divide-bk-50 text-sm leading-[1.4] tracking-[-0.07px]">
          {doc.summary.map(({ label, value }, index) => (
            <div
              key={index}
              className="flex flex-col gap-1 py-3.5 first:pt-0 last:pb-0"
            >
              <dt className="font-medium text-bk-600">{label}</dt>
              <dd className="break-keep text-bk-900">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>
    )}

    <Card className="flex flex-col gap-6 px-5 pb-7 pt-6">
      {/* 문서는 생성된 고정 데이터라 순서가 바뀌지 않는다 — 키는 전부 자리다. 글자를 키로
          쓰면 개정본에 같은 문장이 두 번 나올 때 충돌한다. */}
      {doc.articles.map((article, articleIndex) => (
        <section key={articleIndex} className="flex flex-col gap-2">
          <h2 className="break-keep text-base font-medium leading-[1.4] tracking-[-0.08px] text-bk-900">
            {article.title}
          </h2>
          {article.blocks.map((block, index) => (
            <Block key={index} block={block} articleTitle={article.title} />
          ))}
        </section>
      ))}
    </Card>
  </div>
);
